import {
  FC,
  ReactNode,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';

import { store } from 'redux/store';
import { useTranslate } from 'hooks/useTranslate';
import errorSwal from 'utils/errorSwal';
import successSwal from 'utils/successSwal';

import sizes from 'constants/sizes';
import { setModal } from 'modules/_shared/redux/modalActions';
import { updateDocument } from 'modules/documents/redux/documentActions';
import { ensureIncentivesMarketSharePurchaseContractTemplate } from 'modules/incentivesMarket/api/ensureIncentivesMarketSharePurchaseContractTemplate';

import { editIncentivesMarketSharePurchaseContractTemplateModalInitValue } from '../constants/editIncentivesMarketSharePurchaseContractTemplateModalInitValue';
import { EditIncentivesMarketSharePurchaseContractTemplateModalContextType } from '../types/EditIncentivesMarketSharePurchaseContractTemplateModalContextType';

export const EditIncentivesMarketSharePurchaseContractTemplateModalContext =
  createContext<EditIncentivesMarketSharePurchaseContractTemplateModalContextType>(
    editIncentivesMarketSharePurchaseContractTemplateModalInitValue
  );

const normalizeEditorModel = (editorModel: unknown): string => {
  if (editorModel == null) return '';
  if (typeof editorModel === 'string') return editorModel;
  return '';
};

export const EditIncentivesMarketSharePurchaseContractTemplateModalProvider: FC<{
  children: ReactNode;
  societyId: string;
  size?: string;
}> = ({ children, societyId, size = sizes.XL }) => {
  const { t } = useTranslate();

  const [documentId, setDocumentId] = useState<string | null>(null);
  const [contractText, setContractText] = useState('');
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [loadError, setLoadError] = useState(false);
  const [editorInstance, setEditorInstance] = useState<any>(null);

  const handleCloseModal = useCallback(() => {
    store.dispatch(setModal(null));
  }, []);

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      try {
        setIsLoading(true);
        setLoadError(false);
        const doc = await ensureIncentivesMarketSharePurchaseContractTemplate(
          societyId
        );
        if (cancelled || !doc?._id) {
          if (!cancelled && !doc?._id) setLoadError(true);
          return;
        }
        setDocumentId(doc._id);
        setContractText(normalizeEditorModel(doc.editorModel));
      } catch {
        if (!cancelled) setLoadError(true);
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };

    if (societyId) load();

    return () => {
      cancelled = true;
    };
  }, [societyId]);

  const onEditorChange = useCallback(() => {
    if (editorInstance) {
      const content = editorInstance.getData();
      setContractText(content);
    }
  }, [editorInstance]);

  const insertVariable = useCallback(
    (token: string) => {
      if (!editorInstance) return;

      editorInstance.model.change((writer: any) => {
        const insertPosition =
          editorInstance.model.document.selection.getFirstPosition();
        writer.insertText(token, insertPosition);
      });

      editorInstance.editing.view.focus();
    },
    [editorInstance]
  );

  const handleSave = useCallback(async () => {
    if (!documentId) {
      errorSwal(t('IncentivesMarketContractTemplateSaveError'));
      return;
    }

    const currentContent = editorInstance
      ? editorInstance.getData()
      : contractText;

    try {
      setIsSaving(true);
      const updated = (await store.dispatch(
        updateDocument(
          {
            _id: documentId,
            editorModel: currentContent,
          },
          false
        )
      )) as { _id?: string } | undefined;
      if (updated?._id) {
        successSwal(t('IncentivesMarketContractTemplateSaved'));
        handleCloseModal();
      } else {
        errorSwal(t('IncentivesMarketContractTemplateSaveError'));
      }
    } catch {
      errorSwal(t('IncentivesMarketContractTemplateSaveError'));
    } finally {
      setIsSaving(false);
    }
  }, [contractText, editorInstance, documentId, handleCloseModal, t]);

  const contextValue: EditIncentivesMarketSharePurchaseContractTemplateModalContextType =
    useMemo(
      () => ({
        data: {
          societyId,
          documentId,
          contractText,
          isLoading,
          isSaving,
          loadError,
          editorInstance,
        },
        actions: {
          handleCloseModal,
          setContractText,
          insertVariable,
          setEditorInstance,
          onEditorChange,
          handleSave,
        },
      }),
      [
        societyId,
        documentId,
        contractText,
        isLoading,
        isSaving,
        loadError,
        editorInstance,
        handleCloseModal,
        insertVariable,
        onEditorChange,
        handleSave,
      ]
    );

  return (
    <EditIncentivesMarketSharePurchaseContractTemplateModalContext.Provider
      value={contextValue}
    >
      {children}
    </EditIncentivesMarketSharePurchaseContractTemplateModalContext.Provider>
  );
};

export const useEditIncentivesMarketSharePurchaseContractTemplateModal = () =>
  useContext(EditIncentivesMarketSharePurchaseContractTemplateModalContext);
